import {
    useDeleteProductMutation,
    useToggleProductFieldMutation,
    useDuplicateProductMutation
} from '@services/product/product/product.endpoints';
import { AgendaProductTable } from '@services/product/product/product.types';
import { PaginationMetadata } from '@services/providers/cosem.types';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

export const useAgendaListPageVM = () => {
    const [totalEvents, setTotalEvents] = useState<number>(0);
    const columnHelper = createColumnHelper<AgendaProductTable>();
    const [needReload, setNeedReload] = useState<boolean>();
    const navigate = useNavigate();

    const MySwal = withReactContent(Swal);

    const [toggleProductField, { isSuccess: isToggleProductFieldSuccess, isLoading: isToggleProductFieldLoading }] =
        useToggleProductFieldMutation();
    const [deleteProduct, { isSuccess: isDeleteProductSuccess, isLoading: isDeleteProductLoading }] = useDeleteProductMutation();
    const [
        duplicateProduct,
        { isSuccess: isDuplicateProductSuccess, data: duplicateProductResponse, isLoading: isDuplicateProductLoading }
    ] = useDuplicateProductMutation();

    useEffect(() => {
        if (isToggleProductFieldSuccess || isDeleteProductSuccess) {
            setNeedReload((prev) => !prev);
        }
    }, [isToggleProductFieldSuccess, isDeleteProductSuccess]);


    useEffect(() => {
        if (isDuplicateProductSuccess && duplicateProductResponse) {
            navigate(`/products-management/products/${duplicateProductResponse.data.id}/edit`);
        }
    }, [isDuplicateProductSuccess, duplicateProductResponse, navigate]);

    const handleMetadata = (metadata: PaginationMetadata) => {
        if (metadata && metadata.total !== undefined) {
            setTotalEvents(metadata.total);
        }
    };

    const handleDeleteProduct = (id: number) => {
        MySwal.fire({
            title: 'Êtes-vous sûr de vouloir supprimer cet événement ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: 'Supprimer',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteProduct({ id });
            }
        });
    };

    const handleDeleteProducts = (lines: AgendaProductTable[]) => {
        MySwal.fire({
            title: 'Êtes-vous sûr de vouloir supprimer ces événements ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: 'Supprimer',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                lines.forEach((line) => {
                    deleteProduct({ id: line.id });
                });
            }
        });
    };

    const handleDuplicateProduct = (id: number) => {
        MySwal.fire({
            title: 'Êtes-vous sûr de vouloir dupliquer cet événement ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: 'Dupliquer',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                duplicateProduct({ id });
            }
        });
    };

    return {
        totalEvents,
        setTotalEvents,
        columnHelper,
        needReload,
        navigate,
        toggleProductField,
        handleMetadata,
        handleDeleteProduct,
        handleDuplicateProduct,
        handleDeleteProducts,
        isLoading: isToggleProductFieldLoading || isDeleteProductLoading || isDuplicateProductLoading
    };
};
